/**
 * @fileoverview Auth Store
 * @description Zustand store for authentication state management
 * @module @xtream/frontend/store/auth
 *
 * Features:
 * - User session management
 * - Token storage with localStorage persistence
 * - Login/logout actions
 * - Token refresh support
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { IUser, ITokenPair, UserRole } from '@xtream/shared-types';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Auth store state
 */
interface AuthState {
  /** Current authenticated user */
  user: IUser | null;
  /** JWT access token */
  accessToken: string | null;
  /** JWT refresh token */
  refreshToken: string | null;
  /** Token expiration in seconds */
  expiresIn: number | null;
  /** Token expiration timestamp */
  expiresAt: number | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth state is being loaded */
  isLoading: boolean;
  /** Whether a login is in progress */
  isLoggingIn: boolean;
  /** Auth error message */
  error: string | null;
}

/**
 * Auth store actions
 */
interface AuthActions {
  /** Set user and tokens after successful login */
  login: (user: IUser, tokens: ITokenPair) => void;
  /** Clear user and tokens on logout */
  logout: () => void;
  /** Update access token (used for token refresh) */
  updateAccessToken: (accessToken: string, expiresIn: number) => void;
  /** Update refresh token */
  updateRefreshToken: (refreshToken: string) => void;
  /** Update user data */
  updateUser: (user: Partial<IUser>) => void;
  /** Set loading state */
  setLoading: (isLoading: boolean) => void;
  /** Set logging in state */
  setLoggingIn: (isLoggingIn: boolean) => void;
  /** Set error */
  setError: (error: string | null) => void;
  /** Clear error */
  clearError: () => void;
  /** Check if token is expired */
  isTokenExpired: () => boolean;
  /** Check if user has required role */
  hasRole: (role: UserRole) => boolean;
  /** Check if user has any of the required roles */
  hasAnyRole: (roles: UserRole[]) => boolean;
  /** Get time until token expires (in seconds) */
  getTokenExpiresIn: () => number;
}

// ═══════════════════════════════════════════════════════════════════════════
// INITIAL STATE
// ═══════════════════════════════════════════════════════════════════════════

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  expiresIn: null,
  expiresAt: null,
  isAuthenticated: false,
  isLoading: true,
  isLoggingIn: false,
  error: null,
};

// ═══════════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Zustand auth store with localStorage persistence
 */
export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      login: (user, tokens) => {
        const expiresAt = Date.now() + tokens.expiresIn * 1000;
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresIn: tokens.expiresIn,
          expiresAt,
          isAuthenticated: true,
          isLoading: false,
          isLoggingIn: false,
          error: null,
        });
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          expiresIn: null,
          expiresAt: null,
          isAuthenticated: false,
          isLoading: false,
          isLoggingIn: false,
          error: null,
        });
      },

      updateAccessToken: (accessToken, expiresIn) => {
        const expiresAt = Date.now() + expiresIn * 1000;
        set({
          accessToken,
          expiresIn,
          expiresAt,
        });
      },

      updateRefreshToken: (refreshToken) => {
        set({ refreshToken });
      },

      updateUser: (userData) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          });
        }
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setLoggingIn: (isLoggingIn) => {
        set({ isLoggingIn });
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      isTokenExpired: () => {
        const { expiresAt, accessToken } = get();
        if (!accessToken || !expiresAt) return true;
        // Add 30-second buffer before actual expiration
        return Date.now() >= expiresAt - 30_000;
      },

      hasRole: (role) => {
        const { user } = get();
        return user?.role === role;
      },

      hasAnyRole: (roles) => {
        const { user } = get();
        if (!user) return false;
        return roles.includes(user.role);
      },

      getTokenExpiresIn: () => {
        const { expiresAt } = get();
        if (!expiresAt) return 0;
        const remaining = (expiresAt - Date.now()) / 1000;
        return Math.max(0, Math.floor(remaining));
      },
    }),
    {
      name: 'xtream-auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist auth-related data
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresIn: state.expiresIn,
        expiresAt: state.expiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
      // On rehydration, mark as not loading
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isLoading = false;
          // Check if stored token is expired
          if (state.isTokenExpired()) {
            // Don't auto-logout, let the app handle refresh
            state.isLoading = false;
          }
        }
      },
    },
  ),
);

// ═══════════════════════════════════════════════════════════════════════════
// SELECTORS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Selector: Get current user
 */
export const selectUser = (state: AuthState & AuthActions) => state.user;

/**
 * Selector: Get authentication status
 */
export const selectIsAuthenticated = (state: AuthState & AuthActions) => state.isAuthenticated;

/**
 * Selector: Get access token
 */
export const selectAccessToken = (state: AuthState & AuthActions) => state.accessToken;

/**
 * Selector: Get loading state
 */
export const selectIsLoading = (state: AuthState & AuthActions) => state.isLoading;

/**
 * Selector: Get error
 */
export const selectError = (state: AuthState & AuthActions) => state.error;

export default useAuthStore;
